import '../global.css'
import '@vio/atlas/components/designSystem/helpers/Box/Box.styles.css'

import React, {ReactNode, useRef} from 'react'
import {Provider} from 'react-redux'
import AppServerProviders from 'AppServerProviders'
import InjectPageHeadTags from 'components/Seo/InjectPageHeadTags'
import {TrackVercelCanaryRelease} from 'components/TrackVercelCanaryRelease/TrackVercelCanaryRelease'
import {PagePropsContext} from 'context/pageProps'
import {useCreateServerToggle} from 'hooks/useServerToggle'
import dynamic from 'next/dynamic'
import Error, {ErrorProps} from 'next/error'
import Head from 'next/head'
import {useRouter} from 'next/router'
// eslint-disable-next-line no-restricted-imports
import {toggle} from 'opticks'
import {initializeStore, RootState, Store} from 'store'
import {getGoogleTagMeasurementId} from 'utils/googleTag'
import {getCanonicalUrl} from 'utils/seo/getCanonicalUrl'
import {getStructuredData} from 'utils/seo/getStructuredData/getStructuredData'

import GridGlobalStyles from '@vio/atlas/foundation/GridGlobalStyles'
import {CssBaseline} from '@vio/atlas/helpers/CssBaseline'
import {useThemeOverride} from '@vio/core/src/_web/brand/hooks/useThemeOverride'
import {BOOKINGS_PATH} from '@vio/core/src/booking/constants'
import {defaultBrand} from '@vio/core/src/brand/config'
import {ServerExperimentsContext} from '@vio/core/src/experiments/context/ServerExperimentsContext'
import {getSapiClient} from '@vio/core/src/sapi/services'
import {noop} from '@vio/core/src/utils/function'
import {ScenarioSimulatorDeferredLoader} from '@vio/debug-panel/src/utils/scenarioSimulator/ScenarioSimulatorDeferredLoader'
import {loadResizeObserverPolyfill} from '@vio/shared/src/utils/polyfills/resizeObserver'

import type {AppProps} from 'next/app'
import type {AllPageProps} from 'types/PageProps'
import type {Toggle} from 'types/Toggle'

/**
 * Global components and hooks, loaded client-side only to ease the migration from CRA
 */
const AppGlobals = dynamic(
  () =>
    import(
      /* webpackPrefetch: true */
      '../AppGlobals'
    ),
  {ssr: false}
)

const DefaultLayout = ({children}: {children: ReactNode}) => <>{children}</>
const UserPageLayout = dynamic(() => import('views/user/UserPageLayout'), {
  ssr: false
})
const BookingPageLayout = dynamic(
  () => import('views/bookings/BookingPageLayout'),
  {ssr: false}
)

const AppOrError = (props: AppProps<AllPageProps | ErrorProps>) => {
  if ('statusCode' in props.pageProps) {
    return <Error {...props.pageProps} />
  } else {
    return <App {...(props as AppProps<AllPageProps>)} />
  }
}

const scenarioSimulatorLoader = () => import('mocks/initializeSimulator')

// Fire and forget at module scope. Most BottomSheets mount on a click, so the
// polyfill is almost always in place first. Sheets that open straight after
// hydration (restored `?overlay=` state, Deal Freeze resume, Price Watch
// onboarding) can in theory beat it, and react-modal-sheet would then throw.
loadResizeObserverPolyfill()

const App = ({Component, pageProps}: AppProps<AllPageProps>) => {
  const {
    brand = defaultBrand,
    page,
    isCachedPage,
    shouldLoadSimulator
  } = pageProps
  const preloadedState = pageProps.preloadedReduxState as unknown as RootState

  const {pathname} = useRouter()
  const getLayout = () => {
    if (pathname.startsWith('/user')) return UserPageLayout
    // Booking Invoice page doesn't use BookingPageLayout (no header/footer for print)
    if (pathname.endsWith('/invoice')) return DefaultLayout
    if (pathname.startsWith(BOOKINGS_PATH)) return BookingPageLayout
    return DefaultLayout
  }
  const Layout = getLayout()

  const canonicalUrl = getCanonicalUrl(brand, page)
  const structuredData = getStructuredData(brand.code, page)
  const themeOverride = useThemeOverride(process.env.NEXT_PUBLIC_ENV)
  const cssThemeOverridesHost = process.env.NEXT_PUBLIC_CSS_THEME_OVERRIDES_URL

  const {cookieConsent} = brand.capabilities.compliance
  const googleTagMeasurementId = getGoogleTagMeasurementId(
    brand.capabilities.tracking.googleAnalytics
  )

  const serverToggle = useCreateServerToggle({preloadedState, pageProps})
  const experimentContextRef = useRef({
    isCachedPage,
    serverToggle,
    presetExperiments: pageProps.experiments
  })

  const storeRef = useRef<Store | null>(null)
  if (!storeRef.current) {
    storeRef.current = initializeStore(
      preloadedState,
      typeof window === 'undefined'
        ? {
            toggle: noop as Toggle,
            getSapiClient: noop as typeof getSapiClient
          }
        : {
            toggle,
            getSapiClient
          }
    )
  }

  return (
    <>
      <Head>
        <script
          type="text/javascript"
          id="js-canary"
          dangerouslySetInnerHTML={{
            __html: `document.documentElement.dataset.jsEnabled = '1';`
          }}
        />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, shrink-to-fit=no"
        />
        <link
          rel="icon"
          href={`/static/brands/${brand.code}_favicon.png?v=2`}
          sizes="any"
        />
        <link
          id="brand-theme"
          rel="stylesheet"
          href={`/assets/css/${brand.theme}.css`}
        />
        {Boolean(canonicalUrl) && <link rel="canonical" href={canonicalUrl} />}
        {structuredData.map((data, index) => (
          <script
            key={`structured-data-${index}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{__html: data}}
          />
        ))}
        {process.env.NODE_ENV === 'development' && (
          // Load as a CSS file because the import in JS isn't extracted to CSS during development
          <link
            rel="stylesheet"
            href={`/assets/css/react-loading-skeleton.css`}
          />
        )}
        {Boolean(themeOverride) && cssThemeOverridesHost && (
          <link
            rel="stylesheet"
            href={`${cssThemeOverridesHost}/${themeOverride}.css`}
            id="theme-override"
          />
        )}
        {cookieConsent.enabled ? (
          <>
            <script
              type="text/javascript"
              id="osano-consent-mode"
              dangerouslySetInnerHTML={{
                __html: `
            window.dataLayer = window.dataLayer ||[];
            function gtag(){dataLayer.push(arguments);}
            gtag('consent','default',{
              'ad_storage':'denied',
              'analytics_storage':'denied',
              'ad_user_data':'denied',
              'ad_personalization':'denied',
              'personalization_storage':'denied',
              'functionality_storage':'granted',
              'security_storage':'granted',
              'wait_for_update': 500
            });
            gtag("set", "ads_data_redaction", true);
          `
              }}
            />
            {googleTagMeasurementId && (
              <script
                async
                id="google-tag"
                src={`https://www.googletagmanager.com/gtag/js?id=${googleTagMeasurementId}`}
              />
            )}
            {googleTagMeasurementId && (
              <script
                id="google-tag-config"
                dangerouslySetInnerHTML={{
                  __html: `gtag('js',new Date());gtag('config','${googleTagMeasurementId}');`
                }}
              />
            )}
            <style>{`.osano-cm-dialog,.osano-cm-widget{display: none;}`}</style>
            <script
              type="text/javascript"
              id="osano-cross-device-consent"
              dangerouslySetInnerHTML={{
                __html: `
            ;(function(w,o,d){w[o]=w[o]||function(){w[o][d].push(arguments)};w[o][d]=w[o][d]||[]})(window,'Osano','data');
            if(window.osanoMobileAppIdentifier) {
              window.Osano('userData', window.osanoMobileAppIdentifier);
            }`
              }}
            />
            <link
              href={`https://cmp.osano.com/AzZuEAUQXGtUb3YcB/${cookieConsent.config.id}/osano.js`}
              as="script"
              rel="preload"
            />
            <script
              async
              type="text/javascript"
              src={`https://cmp.osano.com/AzZuEAUQXGtUb3YcB/${cookieConsent.config.id}/osano.js`}
            />
          </>
        ) : null}
        {brand.meta &&
          Object.entries(brand.meta).map(([key, value]) => (
            <meta key={key} name={key} content={value} />
          ))}
      </Head>
      <PagePropsContext.Provider value={pageProps}>
        <ServerExperimentsContext.Provider value={experimentContextRef.current}>
          <Provider store={storeRef.current}>
            <AppServerProviders>
              <TrackVercelCanaryRelease />
              <CssBaseline />
              <GridGlobalStyles />
              <InjectPageHeadTags />
              <AppGlobals />
              <ScenarioSimulatorDeferredLoader
                initializer={scenarioSimulatorLoader}
                shouldLoadSimulator={shouldLoadSimulator}
              >
                <Layout>
                  <Component {...pageProps} />
                </Layout>
              </ScenarioSimulatorDeferredLoader>
            </AppServerProviders>
          </Provider>
        </ServerExperimentsContext.Provider>
      </PagePropsContext.Provider>
    </>
  )
}

export default AppOrError
